import React, { useEffect, useState } from "react";
import { connect } from "react-redux";
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
} from '@dnd-kit/core';
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  useSortable,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import VVVItemsWidget from "./Items/Components/widget";
import ErvaringenWidget from "./Ervaringen/Components/widget";
import BoekingenWidget from "./Boekingen/Components/widget";
import ErvaringenWidgetAankomende from "./Ervaringen/Components/widgetaankomende";
import Loading from "../Components/loading/load";

// Sortable Widget Item Component
const SortableWidgetItem = ({ widget, index, toggleWidgetVisibility, toggleWidgetSize }) => {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: widget.id });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.8 : 1,
  };

  return (
    <div ref={setNodeRef} style={style} className="col-md-6">
      <div className={`border rounded p-3 ${widget.visible ? 'bg-light' : 'bg-light-secondary'} ${isDragging ? 'shadow' : ''}`}>
        <div className="d-flex justify-content-between align-items-center mb-2">
          <div className="d-flex align-items-center gap-2">
            <div {...attributes} {...listeners} className="drag-handle" title="Sleep om te herordenen">
              <i className="bx bx-grid-vertical"></i>
            </div>
            <span className="badge bg-secondary">{index + 1}</span>
            <small className="fw-semibold">{widget.title}</small>
          </div>
          <div className="form-check form-switch">
            <input
              className="form-check-input"
              type="checkbox"
              checked={widget.visible}
              onChange={() => toggleWidgetVisibility(widget.id)}
            />
          </div>
        </div>
        {widget.visible && (
          <div className="d-flex gap-1">
            <button
              className={`btn btn-xs ${widget.size === 'col-md-12' ? 'btn-primary' : 'btn-outline-primary'}`}
              onClick={() => toggleWidgetSize(widget.id)}
            >
              Groot
            </button>
            <button
              className={`btn btn-xs ${widget.size === 'col-xl-6' ? 'btn-primary' : 'btn-outline-primary'}`}
              onClick={() => toggleWidgetSize(widget.id)}
            >
              Klein
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

const Startpage = ({ user, history }) => {
  const [load, setLoad] = useState(true);
  const [widgets, setWidgets] = useState([]);
  const [editMode, setEditMode] = useState(false);

  const sensors = useSensors(
    useSensor(PointerSensor),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  );

  const vaild = (vall) => {
    return vall.includes(user.role);
  };

  const ismodule = (val) => {
    return user.bedrijf && user.bedrijf.modules && user.bedrijf.modules.includes(val);
  };

  // Default widget configuration
  const getDefaultWidgets = () => {
    const defaultWidgets = [];
    let index = 0;

    if (vaild(["admin", "manager", "medewerker", "faciliteitmedewerker", "locatiebeheerder"]) && ismodule("items")) {
      defaultWidgets.push({
        id: `widget-${index++}`,
        type: 'vvv-items',
        title: 'VVV Items',
        size: 'col-xl-6',
        order: defaultWidgets.length,
        visible: true
      });
    }

    if (vaild(["admin", "manager", "medewerker", "ervaringeigenaar"]) && ismodule("ervaringen")) {
      defaultWidgets.push({
        id: `widget-${index++}`,
        type: 'ervaringen-aankomende',
        title: 'Aankomende ervaringen',
        size: 'col-xl-6',
        order: defaultWidgets.length,
        visible: true
      });
    }

    if (vaild(["admin", "manager", "medewerker", "ervaringeigenaar"]) && ismodule("ervaringen")) {
      defaultWidgets.push({
        id: `widget-${index++}`,
        type: 'boekingen',
        title: 'Overzicht nieuwe boekingen',
        size: 'col-md-12',
        order: defaultWidgets.length,
        visible: true
      });
    }

    if (vaild(["admin", "manager", "medewerker", "ervaringeigenaar"]) && ismodule("ervaringen")) {
      defaultWidgets.push({
        id: `widget-${index++}`,
        type: 'ervaringen',
        title: 'Ervaringen',
        size: 'col-md-12',
        order: defaultWidgets.length,
        visible: true
      });
    }

    return defaultWidgets;
  };

  // Load widgets from localStorage or use defaults
  useEffect(() => {
    const savedWidgets = localStorage.getItem('dashboard-widgets');
    if (savedWidgets) {
      try {
        const parsedWidgets = JSON.parse(savedWidgets);
        setWidgets(parsedWidgets);
      } catch (error) {
        console.error('Error parsing saved widgets:', error);
        setWidgets(getDefaultWidgets());
      }
    } else {
      setWidgets(getDefaultWidgets());
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user]); // Re-run when user changes

  // Save widgets to localStorage
  const saveWidgets = (updatedWidgets) => {
    localStorage.setItem('dashboard-widgets', JSON.stringify(updatedWidgets));
    setWidgets(updatedWidgets);
  };

  // Handle drag end
  const handleDragEnd = (event) => {
    const { active, over } = event;

    if (active.id !== over?.id) {
      const sortedWidgets = [...widgets].sort((a, b) => a.order - b.order);
      const oldIndex = sortedWidgets.findIndex((w) => w.id === active.id);
      const newIndex = sortedWidgets.findIndex((w) => w.id === over.id);

      const reorderedWidgets = arrayMove(sortedWidgets, oldIndex, newIndex).map((widget, index) => ({
        ...widget,
        order: index
      }));

      saveWidgets(reorderedWidgets);
    }
  };

  // Toggle widget size
  const toggleWidgetSize = (widgetId) => {
    const updatedWidgets = widgets.map(widget => {
      if (widget.id === widgetId) {
        const newSize = widget.size === 'col-md-12' ? 'col-xl-6' : 'col-md-12';
        return { ...widget, size: newSize };
      }
      return widget;
    });
    saveWidgets(updatedWidgets);
  };

  // Toggle widget visibility
  const toggleWidgetVisibility = (widgetId) => {
    const updatedWidgets = widgets.map(widget => {
      if (widget.id === widgetId) {
        return { ...widget, visible: !widget.visible };
      }
      return widget;
    });
    saveWidgets(updatedWidgets);
  };

  // Reset to default layout
  const resetLayout = () => {
    if (window.confirm('Wilt u het dashboard terugzetten naar de standaard layout?')) {
      const defaultWidgets = getDefaultWidgets();
      saveWidgets(defaultWidgets);
    }
  };

  // Render widget content based on type
  const renderWidgetContent = (widget) => {
    switch (widget.type) {
      case 'vvv-items':
        return <VVVItemsWidget history={history} />;
      case 'ervaringen-aankomende':
        return <ErvaringenWidgetAankomende />;
      case 'boekingen':
        return <BoekingenWidget />;
      case 'ervaringen':
        return <ErvaringenWidget />;
      default:
        return null;
    }
  };

  // Filter and sort widgets
  const visibleWidgets = widgets
    .filter(widget => widget.visible)
    .sort((a, b) => a.order - b.order);

  // Bereken effectieve kolombreedte: als een col-xl-6 widget alleen op een rij staat, maak het col-md-12
  const getEffectiveSize = (index) => {
    const widget = visibleWidgets[index];
    if (widget.size !== 'col-xl-6') return widget.size;
    // Tel opeenvolgende col-xl-6 widgets in dezelfde groep
    // Een groep begint na een niet-col-xl-6 widget of aan het begin
    let groupStart = index;
    while (groupStart > 0 && visibleWidgets[groupStart - 1].size === 'col-xl-6') {
      groupStart--;
    }
    let groupEnd = index;
    while (groupEnd < visibleWidgets.length - 1 && visibleWidgets[groupEnd + 1].size === 'col-xl-6') {
      groupEnd++;
    }
    const groupSize = groupEnd - groupStart + 1;
    const positionInGroup = index - groupStart;
    // Als de groep oneven is en dit is het laatste item, maak het full-width
    if (groupSize % 2 === 1 && positionInGroup === groupSize - 1) return 'col-md-12';
    return 'col-xl-6';
  };

  const sortedWidgets = [...widgets].sort((a, b) => a.order - b.order);

  return (
    <>
      {/* Dashboard Controls */}
      <div className="row mb-3">
        <div className="col-12">
          <div className="card">
            <div className="card-body py-2">
              <div className="d-flex justify-content-between align-items-center">
                <h6 className="mb-0">
                  <i className="bx bx-dashboard me-2"></i>Dashboard
                </h6>
                <div className="d-flex gap-2">
                  <button
                    className={`btn btn-sm ${editMode ? 'btn-success' : 'btn-outline-primary'}`}
                    onClick={() => setEditMode(!editMode)}
                  >
                    <i className={`bx ${editMode ? 'bx-check' : 'bx-edit'} me-1`}></i>
                    {editMode ? 'Klaar' : 'Bewerken'}
                  </button>
                  {editMode && (
                    <button
                      className="btn btn-sm btn-outline-secondary"
                      onClick={resetLayout}
                    >
                      <i className="bx bx-reset me-1"></i>Reset
                    </button>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Widget Configuration Panel */}
      {editMode && (
        <div className="row mb-3">
          <div className="col-12">
            <div className="card">
              <div className="card-header">
                <h6 className="card-title mb-0">Widget Instellingen</h6>
              </div>
              <div className="card-body">
                <DndContext
                  sensors={sensors}
                  collisionDetection={closestCenter}
                  onDragEnd={handleDragEnd}
                >
                  <SortableContext
                    items={sortedWidgets.map(w => w.id)}
                    strategy={verticalListSortingStrategy}
                  >
                    <div className="row g-2">
                      {sortedWidgets.map((widget, index) => (
                        <SortableWidgetItem
                          key={widget.id}
                          widget={widget}
                          index={index}
                          toggleWidgetVisibility={toggleWidgetVisibility}
                          toggleWidgetSize={toggleWidgetSize}
                        />
                      ))}
                    </div>
                  </SortableContext>
                </DndContext>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Widgets */}
      <div className="row">
        {visibleWidgets.map((widget, index) => (
          <div key={widget.id} className={getEffectiveSize(index)}>
            <div className={`box ${getBoxClass(widget.type)} mb-3`}>
              <div className="box-header with-border">
                <h3 className="box-title">{widget.title}</h3>
              </div>
              <div className="box-body">
                {renderWidgetContent(widget)}
                <Loading loadings={load} />
              </div>
            </div>
          </div>
        ))}

        {visibleWidgets.length === 0 && (
          <div className="col-12 text-center py-5">
            <div className="card">
              <div className="card-body">
                <div className="text-muted">
                  <i className="bx bx-ghost bx-lg mb-3"></i>
                  <p>Geen widgets zichtbaar. Activeer widgets in de instellingen hierboven.</p>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Custom CSS */}
      <style dangerouslySetInnerHTML={{__html: `
        .btn-xs {
          padding: 0.125rem 0.25rem;
          font-size: 0.75rem;
        }
        .drag-handle {
          cursor: grab;
          padding: 6px;
          border-radius: 4px;
          background-color: rgba(108, 117, 125, 0.1);
          border: 1px solid rgba(108, 117, 125, 0.2);
          transition: all 0.2s ease;
          display: inline-flex;
          align-items: center;
          justify-content: center;
          min-width: 24px;
          height: 24px;
        }
        .drag-handle i {
          color: #6c757d;
          font-size: 14px;
        }
        .drag-handle:hover {
          background-color: rgba(108, 117, 125, 0.2);
          border-color: rgba(108, 117, 125, 0.4);
        }
        .drag-handle:hover i {
          color: #495057;
        }
        .drag-handle:active {
          cursor: grabbing;
          background-color: rgba(108, 117, 125, 0.3);
        }
      `}} />
    </>
  );
};

// Helper function to get box class based on widget type
const getBoxClass = (type) => {
  switch (type) {
    case 'vvv-items':
    case 'ervaringen-aankomende':
    case 'boekingen':
    case 'ervaringen':
      return 'box-info';
    default:
      return 'box-info';
  }
};

function mapStateToProps(state) {
  return { user: state.auth.user };
}

export default connect(mapStateToProps)(Startpage);
